<template lang="html">
  <section class="about">
    <h1 class="title">About</h1>
    <div class="content">
      <p>
        {{ appName }} makes it easy to practice Blackjack basic strategy and
        card counting using the
        <RemoteLink
          href="https://wizardofodds.com/games/blackjack/card-counting/high-low"
          >Hi-Lo system</RemoteLink
        >. Cards are dealt according to a
        <RemoteLink href="https://github.com/mhluska/blackjack-simulator">
          fully realistic Blackjack simulation</RemoteLink
        >.
      </p>

      <p>
        Alternate game modes are also available which allow practicing
        deviations and unintuitive areas of basic strategy.
      </p>

      <h2 id="basic-strategy">Basic Strategy</h2>

      <p>
        {{ appName }} uses
        <RemoteLink
          href="https://github.com/mhluska/blackjack-simulator/blob/master/src/charts/multi-deck/h17-das.ts"
        >
          correct basic strategy rules</RemoteLink
        >
        for every game mode including single deck, double deck and multi deck:
      </p>

      <!-- TODO: Add in-app HTML-based charts for each of these. Generate them
      automatically from blackjack-simulator code. -->
      <ul>
        <li>
          <a :href="require('@/assets/images/charts/1d-s17.gif')">
            Single-deck, stands soft 17
          </a>
        </li>
        <li>
          <a :href="require('@/assets/images/charts/1d-h17.gif')">
            Single-deck, hits soft 17
          </a>
        </li>
        <li>
          <a :href="require('@/assets/images/charts/2d-s17.gif')">
            Double-deck, stands soft 17
          </a>
        </li>
        <li>
          <a :href="require('@/assets/images/charts/2d-h17.gif')">
            Double-deck, hits soft 17
          </a>
        </li>
        <li>
          <a :href="require('@/assets/images/charts/multi-s17.gif')">
            Multi-deck, stands soft 17
          </a>
        </li>
        <li>
          <a :href="require('@/assets/images/charts/multi-h17.gif')">
            Multi-deck, hits soft 17
          </a>
        </li>
      </ul>

      <h2 id="deviations">Deviations</h2>
      <p>
        Basic strategy deviations based on Illustrious 18 and Fab 4 are
        supported:
      </p>

      <FormattedTable :items="deviationsItemsI18" />

      <FormattedTable :items="deviationsItemsFab4" />

      <p>
        If you find this project useful, consider giving a donation or star on
        GitHub.
      </p>
      <div>
        <RemoteLink href="https://github.com/mhluska/blackjack-simulator">
          <img
            alt="GitHub Repo stars"
            src="https://img.shields.io/github/stars/mhluska/blackjack-simulator?style=social"
          />
        </RemoteLink>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';

import RemoteLink from '@/components/RemoteLink';
import FormattedTable from '@/components/FormattedTable';
import { appName } from '@/constants';

export default {
  name: 'About',

  components: {
    RemoteLink,
    FormattedTable,
  },

  data() {
    return {
      appName,

      // TODO: Make this DRY with the deviations the engine uses.
      deviationsItemsI18: [
        {
          'Player Total': '',
          'Dealers Card': 'A',
          'Correct Move': 'Insurance',
          Index: '>= 3',
        },
        {
          'Player Total': 16,
          'Dealers Card': 'T',
          'Correct Move': 'Stand',
          Index: '>= 0',
        },
        {
          'Player Total': 15,
          'Dealers Card': 'T',
          'Correct Move': 'Stand',
          Index: '>= 4',
        },
        {
          'Player Total': 'T/T',
          'Dealers Card': '5',
          'Correct Move': 'Split',
          Index: '>= 5',
        },
        {
          'Player Total': 'T/T',
          'Dealers Card': '6',
          'Correct Move': 'Split',
          Index: '>= 4',
        },
        {
          'Player Total': 10,
          'Dealers Card': 'T',
          'Correct Move': 'Double',
          Index: '>= 4',
        },
        {
          'Player Total': 12,
          'Dealers Card': '3',
          'Correct Move': 'Stand',
          Index: '>= 2',
        },
        {
          'Player Total': 12,
          'Dealers Card': '2',
          'Correct Move': 'Stand',
          Index: '>= 3',
        },
        {
          'Player Total': 11,
          'Dealers Card': 'A',
          'Correct Move': 'Hit',
          Index: '< 1',
        },
        {
          'Player Total': 9,
          'Dealers Card': '2',
          'Correct Move': 'Hit',
          Index: '< 1',
        },
        {
          'Player Total': 10,
          'Dealers Card': 'A',
          'Correct Move': 'Double',
          Index: '>= 4',
        },
        {
          'Player Total': 9,
          'Dealers Card': '7',
          'Correct Move': 'Double',
          Index: '>= 3',
        },
        {
          'Player Total': 16,
          'Dealers Card': '9',
          'Correct Move': 'Stand',
          Index: '>= 5',
        },
        {
          'Player Total': 13,
          'Dealers Card': '2',
          'Correct Move': 'Hit',
          Index: '< -1',
        },
        {
          'Player Total': 12,
          'Dealers Card': '4',
          'Correct Move': 'Hit',
          Index: '< 0',
        },
        {
          'Player Total': 12,
          'Dealers Card': '5',
          'Correct Move': 'Hit',
          Index: '< -2',
        },
        {
          'Player Total': 12,
          'Dealers Card': '6',
          'Correct Move': 'Hit',
          Index: '< -1',
        },
        {
          'Player Total': 13,
          'Dealers Card': '3',
          'Correct Move': 'Hit',
          Index: '< -2',
        },
      ],

      deviationsItemsFab4: [
        {
          'Player Total': 14,
          'Dealers Card': 'T',
          'Correct Move': 'Surrender',
          Index: '>= 3',
        },
        {
          'Player Total': 15,
          'Dealers Card': '9',
          'Correct Move': 'Surrender',
          Index: '>= 2',
        },
        {
          'Player Total': 15,
          'Dealers Card': 'T',
          'Correct Move': 'Surrender',
          Index: '>= 0',
        },
        {
          'Player Total': 15,
          'Dealers Card': 'A',
          'Correct Move': 'Surrender',
          Index: '>= 1',
        },
      ],
    };
  },

  computed: {
    ...mapGetters(['currentUser']),
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/mixins/app-width';

.about {
  @include app-width;
}

.content {
  max-width: map-get($breakpoints, 'phone');
}
</style>
